// src/pages/Tables.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { db } from '../lib/firebase';
import {
  updateDoc, deleteDoc, doc,
  onSnapshot, query, orderBy, collection,
} from 'firebase/firestore';
import { createTable } from '../lib/MerchantModel';
import QRCode from 'react-qr-code';
import { Plus, Download, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_CHECKIE_URL || 'https://checkie.app';
console.log('BASE_URL:', BASE_URL, 'env:', process.env.REACT_APP_CHECKIE_URL);

export default function Tables() {
  const { merchant } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);

  useEffect(() => {
    if (!merchant?.id) return;
    const q = query(
      collection(db, 'merchants', merchant.id, 'tables'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setTables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [merchant?.id]);

  const addTable = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      await createTable(merchant.id, newLabel.trim());
      setNewLabel('');
    } catch (err) { console.error(err); }
    setAdding(false);
  };

  const toggleActive = async (table) => {
    await updateDoc(doc(db, 'merchants', merchant.id, 'tables', table.id), {
      active: !table.active,
    });
  };

  const deleteTable = async (tableId) => {
    if (!window.confirm('Delete this table? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'merchants', merchant.id, 'tables', tableId));
    if (selectedTable?.id === tableId) setSelectedTable(null);
  };

  const downloadQR = (table) => {
    const svg = document.getElementById(`qr-${table.id}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const size = 400;
    const padding = 40;
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, padding, padding, size - padding * 2, size - padding * 2);
      const a = document.createElement('a');
      a.download = `checkie-qr-${table.label.replace(/\s+/g, '-').toLowerCase()}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontWeight: 500, color: '#1a1a1a', marginBottom: '6px' }}>
          Tables & QR Codes
        </h1>
        <p style={{ fontSize: '14px', color: '#888' }}>
          Add your tables and print the QR codes to place on each one.
        </p>
      </div>

      {/* Add table form */}
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid rgba(0,0,0,0.06)', marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>Add a New Table</div>
        <form onSubmit={addTable} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder='e.g. "Table 7" or "Patio 3"'
            required
            style={{
              flex: 1, minWidth: '200px',
              padding: '11px 16px', borderRadius: '10px',
              border: '1px solid rgba(0,0,0,0.1)',
              fontFamily: "'Outfit', sans-serif", fontSize: '14px',
              color: '#1a1a1a', outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = '#4000E4'}
            onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
          />
          <button
            type="submit"
            disabled={adding}
            style={{
              padding: '11px 24px', background: '#4000E4', color: '#fff',
              border: 'none', borderRadius: '10px',
              fontFamily: "'Outfit', sans-serif", fontSize: '14px', fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              opacity: adding ? 0.6 : 1,
            }}
          >
            <Plus size={16} /> Add Table
          </button>
        </form>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#aaa', fontSize: '14px' }}>Loading tables...</div>
      ) : tables.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '60px 24px', border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🪑</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>No tables yet</div>
          <div style={{ fontSize: '14px', color: '#bbb' }}>Add your first table above to generate a QR code.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
          {tables.map(table => (
            <div key={table.id} style={{
              background: '#fff', borderRadius: '16px',
              border: `1px solid ${selectedTable?.id === table.id ? '#4000E4' : 'rgba(0,0,0,0.06)'}`,
              overflow: 'hidden',
              opacity: table.active ? 1 : 0.6,
              transition: 'all 0.2s',
              boxShadow: selectedTable?.id === table.id ? '0 0 0 3px rgba(64,0,228,0.12)' : 'none',
            }}>
              {/* QR Code */}
              <div
                onClick={() => setSelectedTable(selectedTable?.id === table.id ? null : table)}
                style={{
                  padding: '24px', textAlign: 'center', cursor: 'pointer',
                  background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ display: 'inline-block', background: '#fff', padding: '12px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <QRCode
                    id={`qr-${table.id}`}
                    value={`${BASE_URL}/t/${merchant?.id}/${table.qrToken}`}
                    size={120}
                    style={{ display: 'block' }}
                  />
                </div>
              </div>

              {/* Table info */}
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a1a' }}>{table.label}</div>
                  <span style={{
                    fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                    padding: '2px 8px', borderRadius: '20px',
                    background: table.active ? '#f0fdf4' : '#f5f5f5',
                    color: table.active ? '#16a34a' : '#aaa',
                  }}>
                    {table.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {table.qrToken && (
                  <div style={{ fontSize: '11px', color: '#bbb', marginBottom: '12px', fontFamily: 'monospace' }}>
                    token: {table.qrToken}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => downloadQR(table)}
                    style={{
                      flex: 1, padding: '8px', background: '#4000E4', color: '#fff',
                      border: 'none', borderRadius: '8px',
                      fontFamily: "'Outfit', sans-serif", fontSize: '12px', fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    <Download size={13} /> Download
                  </button>
                  <button
                    onClick={() => toggleActive(table)}
                    style={{
                      padding: '8px 12px', background: 'rgba(0,0,0,0.04)', color: '#555',
                      border: 'none', borderRadius: '8px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                    title={table.active ? 'Deactivate' : 'Activate'}
                  >
                    {table.active ? <ToggleRight size={16} color="#4000E4" /> : <ToggleLeft size={16} />}
                  </button>
                  <button
                    onClick={() => deleteTable(table.id)}
                    style={{
                      padding: '8px 12px', background: 'rgba(0,0,0,0.04)', color: '#dc2626',
                      border: 'none', borderRadius: '8px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                    title="Delete table"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}